import React, { useState, useEffect, useRef } from 'react';
import { Mail, Lock, User, Briefcase, Eye, EyeOff, ArrowRight, Loader2, Shield, CreditCard, Building2, Globe, Wallet, Smartphone, ChevronRight, Check, Phone, Volume2, VolumeX, Store } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import LanguageSelector from '../components/LanguageSelector';
import OnerikLogoMedium from '../components/OnerikLogoMedium';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Landing = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login, register, isAuthenticated } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [accountType, setAccountType] = useState('personal');
  const [audioPlayed, setAudioPlayed] = useState(false);
  const [showSoundButton, setShowSoundButton] = useState(false);
  const audioRef = useRef(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    phone: '',
    confirmPassword: '',
    totpCode: ''
  });
  
  const [requires2FA, setRequires2FA] = useState(false);

  // Preload audio on page load for faster playback
  useEffect(() => {
    // Preload the audio file
    const audio = new Audio();
    audio.preload = 'auto';
    audio.src = `${BACKEND_URL}/api/tts/welcome`;
    audio.volume = 0.7;
    audioRef.current = audio;
    
    // Start loading the audio immediately
    audio.load();
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Manual play function - can be played multiple times
  const playWelcomeManually = async () => {
    try {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        await audioRef.current.play();
      } else {
        const audio = new Audio(`${BACKEND_URL}/api/tts/welcome`);
        audio.volume = 0.7;
        await audio.play();
      }
      setAudioPlayed(true);
    } catch (error) {
      console.log('Could not play audio:', error);
    }
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/portfolio');
    }
  }, [isAuthenticated, navigate]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (isLogin) {
      // Login
      const result = await login(
        formData.email, 
        formData.password,
        requires2FA ? formData.totpCode : null
      );
      
      if (result.requires_2fa) {
        setRequires2FA(true);
        toast.info(t('twoFactor.required'));
        setLoading(false);
        return;
      }
      
      if (result.success) {
        toast.success('Welcome back to Onerik!');
        navigate('/portfolio');
      } else {
        toast.error(result.error);
        // If 2FA code was wrong, don't reset the 2FA state
        if (!result.error?.includes('2FA')) {
          setRequires2FA(false);
        }
      }
    } else {
      // Register
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        setLoading(false);
        return;
      }
      if (formData.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        setLoading(false);
        return;
      }
      
      const result = await register(
        formData.email, 
        formData.password, 
        formData.fullName,
        accountType,
        formData.phone
      );
      
      if (result.success) {
        toast.success('Welcome to Onerik! Your account has been created.');
        navigate('/portfolio');
      } else {
        toast.error(result.error);
      }
    }
    
    setLoading(false);
  };

  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#0a0a0a] flex flex-col lg:flex-row landing-page-fixed-dark overflow-x-hidden">
      {/* Top Right - Language Selector & Merchant Portal */}
      <div className="absolute top-4 right-4 z-50 flex items-center gap-3">
        <Link 
          to="/merchant"
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full transition-all text-white text-sm font-medium"
        >
          <Store className="w-4 h-4" />
          <span className="hidden sm:inline">Merchant Portal</span>
        </Link>
        <LanguageSelector />
      </div>

      {/* Left Side - Branding (Hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0a0a0a] relative overflow-hidden">
        {/* Animated gradient blobs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-600/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          {/* Sound Button - visible on top left (desktop) or in mobile logo area */}
          <button
            onClick={playWelcomeManually}
            className="hidden lg:flex fixed top-6 left-6 items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/30 rounded-full transition-all z-50"
            title="Play welcome message"
          >
            <Volume2 className="w-5 h-5 text-white" />
            <span className="text-white text-sm font-medium">Listen</span>
          </button>

          {/* Original Logo */}
          <div className="mb-6">
            <div className="flex items-center gap-4">
              <OnerikLogoMedium size="lg" />
              <div>
                <span className="text-white text-3xl font-bold tracking-tight">Onerik</span>
                <p className="text-white/50 text-sm">Digital Banking</p>
              </div>
            </div>
          </div>

          {/* Hero Text */}
          <h1 className="text-5xl xl:text-6xl font-bold text-white leading-tight mb-6">
            One app.<br />
            <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              All things money.
            </span>
          </h1>
          
          <p className="text-white/60 text-lg mb-10 max-w-md">
            Manage your money, crypto, and investments. All in one place.
          </p>

          {/* Feature List */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-white font-medium">Virtual & Physical Cards</p>
                <p className="text-white/50 text-sm">Spend worldwide with no hidden fees</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-white font-medium">Personal IBAN</p>
                <p className="text-white/50 text-sm">Your own European bank account</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-white font-medium">Crypto Wallets</p>
                <p className="text-white/50 text-sm">Buy, sell, and hold 100+ cryptocurrencies</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <Globe className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-white font-medium">Global Transfers</p>
                <p className="text-white/50 text-sm">Send money anywhere, instantly</p>
              </div>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="mt-12 flex items-center gap-6">
            <div className="flex items-center gap-2 text-white/40 text-sm">
              <Shield className="w-4 h-4" />
              <span>Bank-grade security</span>
            </div>
            <div className="flex items-center gap-2 text-white/40 text-sm">
              <Check className="w-4 h-4" />
              <span>Regulated</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Auth Form */}
      <div className="w-full lg:w-1/2 xl:w-[45%] flex items-center justify-center p-4 sm:p-6 md:p-12 min-h-screen">
        <div className="w-full max-w-md">
          {/* Mobile Logo with Listen button */}
          <div className="lg:hidden mb-4 flex flex-col items-center">
            <div className="w-28 h-28 relative flex items-center justify-center">
              <svg 
                width="100%" 
                height="100%" 
                viewBox="0 0 200 200" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <radialGradient id="logoGradientMobile" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#1e3a8a" stopOpacity="1" />
                    <stop offset="50%" stopColor="#0f172a" stopOpacity="1" />
                    <stop offset="100%" stopColor="#000000" stopOpacity="1" />
                  </radialGradient>
                  <linearGradient id="ringGradientMobile" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#a855f7" />
                    <stop offset="100%" stopColor="#3b82f6" />
                  </linearGradient>
                </defs>
                <circle cx="100" cy="100" r="90" fill="url(#logoGradientMobile)" stroke="url(#ringGradientMobile)" strokeWidth="2" />
                <ellipse cx="100" cy="100" rx="70" ry="30" fill="none" stroke="url(#ringGradientMobile)" strokeWidth="1.5" opacity="0.7" transform="rotate(45 100 100)" />
                <ellipse cx="100" cy="100" rx="70" ry="30" fill="none" stroke="url(#ringGradientMobile)" strokeWidth="1.5" opacity="0.7" transform="rotate(-45 100 100)" />
                <circle cx="100" cy="100" r="15" fill="#a855f7" opacity="0.8" />
                <circle cx="100" cy="100" r="8" fill="#c4b5fd" />
              </svg>
            </div>
            <div className="mt-2 text-center">
              <span className="text-white text-2xl font-bold tracking-tight">Onerik</span>
              <p className="text-white/50 text-sm">All things money, in one app</p>
            </div>
            {/* Mobile Listen button */}
            <button
              onClick={playWelcomeManually}
              className="mt-3 flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/30 rounded-full transition-all"
              title="Play welcome message"
            >
              <Volume2 className="w-4 h-4 text-white" />
              <span className="text-white text-sm font-medium">Listen</span>
            </button>
          </div>

          {/* Auth Card */}
          <div className="bg-[#111111] border border-white/10 rounded-3xl p-5 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
              {isLogin ? 'Welcome back' : 'Create account'}
            </h2>
            <p className="text-white/50 text-sm mb-8">
              {isLogin ? 'Sign in to continue to your account' : 'Start your financial journey today'}
            </p>

            {/* Tabs */}
            <div className="flex mb-8 bg-white/5 rounded-full p-1">
              <button
                onClick={() => setIsLogin(true)}
                className={`flex-1 py-2.5 text-sm font-medium rounded-full transition-all ${
                  isLogin ? 'bg-purple-500/80 text-white' : 'text-white/60 hover:text-white'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => setIsLogin(false)}
                className={`flex-1 py-2.5 text-sm font-medium rounded-full transition-all ${
                  !isLogin ? 'bg-purple-500/80 text-white' : 'text-white/60 hover:text-white'
                }`}
              >
                Sign Up
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Account Type (Sign Up only) */}
              {!isLogin && (
                <div className="grid grid-cols-2 gap-3 mb-2">
                  <button
                    type="button"
                    onClick={() => setAccountType('personal')}
                    className={`p-4 rounded-2xl border-2 transition-all ${
                      accountType === 'personal'
                        ? 'border-purple-500 bg-purple-500/10'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    <User className={`w-5 h-5 mx-auto mb-2 ${accountType === 'personal' ? 'text-purple-400' : 'text-white/60'}`} />
                    <div className="text-sm font-medium text-white">Personal</div>
                    <div className="text-xs text-white/40">For individuals</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAccountType('business')}
                    className={`p-4 rounded-2xl border-2 transition-all ${
                      accountType === 'business'
                        ? 'border-purple-500 bg-purple-500/10'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    <Briefcase className={`w-5 h-5 mx-auto mb-2 ${accountType === 'business' ? 'text-purple-400' : 'text-white/60'}`} />
                    <div className="text-sm font-medium text-white">Business</div>
                    <div className="text-xs text-white/40">For companies</div>
                  </button>
                </div>
              )}

              {/* Full Name (Sign Up only) */}
              {!isLogin && (
                <div>
                  <label className="text-xs font-medium text-white/60 mb-2 block">Full Name</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    required={!isLogin}
                    className="w-full bg-white/5 border border-white/10 focus:border-purple-500/50 rounded-xl h-12 px-4 text-white placeholder:text-white/30 transition-all focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
                  />
                </div>
              )}

              {/* Phone Number (Sign Up only) */}
              {!isLogin && (
                <div>
                  <label className="text-xs font-medium text-white/60 mb-2 block">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1 (555) 000-0000"
                      required={!isLogin}
                      className="w-full bg-white/5 border border-white/10 focus:border-purple-500/50 rounded-xl h-12 pl-11 pr-4 text-white placeholder:text-white/30 transition-all focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
                    />
                  </div>
                  <p className="text-white/30 text-xs mt-1">Used for 2FA verification</p>
                </div>
              )}

            {/* Email */}
            <div>
              <label className="text-xs font-medium text-white/60 mb-2 block">Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="you@example.com"
                required
                className="w-full bg-white/5 border border-white/10 focus:border-purple-500/50 rounded-xl h-12 px-4 text-white placeholder:text-white/30 transition-all focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
              />
            </div>

            {/* Password */}
            <div>
              <label className="text-xs font-medium text-white/60 mb-2 block">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full bg-white/5 border border-white/10 focus:border-purple-500/50 rounded-xl h-12 px-4 pr-12 text-white placeholder:text-white/30 transition-all focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password (Sign Up only) */}
            {!isLogin && (
              <div>
                <label className="text-xs font-medium text-white/60 mb-2 block">Confirm Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  required={!isLogin}
                  minLength={6}
                  className="w-full bg-white/5 border border-white/10 focus:border-purple-500/50 rounded-xl h-12 px-4 text-white placeholder:text-white/30 transition-all focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
                />
              </div>
            )}

            {/* 2FA Code (Login with 2FA enabled) */}
            {isLogin && requires2FA && (
              <div className="animate-in slide-in-from-top-2 duration-200">
                <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 text-purple-400">
                    <Shield className="w-4 h-4" />
                    <span className="text-sm font-medium">Two-Factor Authentication</span>
                  </div>
                  <p className="text-xs text-white/50 mt-1">Enter the code from your authenticator app</p>
                </div>
                <label className="text-xs font-medium text-white/60 mb-2 block">6-Digit Code</label>
                <input
                  type="text"
                  name="totpCode"
                  value={formData.totpCode}
                  onChange={(e) => setFormData({ ...formData, totpCode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  placeholder="000000"
                  maxLength={6}
                  required
                  data-testid="2fa-login-input"
                  className="w-full bg-white/5 border border-white/10 focus:border-purple-500/50 rounded-xl h-14 px-4 text-center text-2xl font-mono text-white tracking-[0.5em] placeholder:text-white/20 transition-all focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
                />
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-500/70 hover:bg-purple-500/90 text-white text-sm font-semibold py-4 rounded-xl transition-all hover:shadow-lg hover:shadow-purple-500/20 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Please wait...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  {isLogin ? 'Continue' : 'Get Started'}
                  <ChevronRight className="w-5 h-5" />
                </span>
              )}
            </button>
          </form>

          {/* Switch Auth Mode */}
          <p className="text-center text-sm text-white/50 mt-6">
            {isLogin ? (
              <>
                New to Onerik?{' '}
                <button
                  onClick={() => setIsLogin(false)}
                  className="text-purple-400 hover:text-purple-300 font-medium transition-colors"
                >
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button
                  onClick={() => setIsLogin(true)}
                  className="text-purple-400 hover:text-purple-300 font-medium transition-colors"
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-white/30 mt-8">
          By continuing, you agree to Onerik&apos;s Terms of Service and Privacy Policy
        </p>
        </div>
      </div>
    </div>
  );
};

export default Landing;
